import { useHistory } from "react-router";
import {
  getNestedChildState,
  useCheckoutState,
  useCheckoutStateSelector as useSelector,
} from "@hopper-b2b/checkout";
import { useI18nContext } from "@hopper-b2b/i18n";
import { ButtonWrap, MobileFloatingSection } from "@hopper-b2b/ui";

import clsx from "clsx";
import ArrowBack from "../../../../../assets/client/arrow-back.svg?react";
import CardTransferIcon from "../../../../../assets/client/card-transfer-limits.svg?react";
import CloseIcon from "../../../../../assets/client/close.svg?react";
import MoneyOutIcon from "../../../../../assets/client/money-out.svg?react";
import PasswordIcon from "../../../../../assets/client/password.svg?react";
import RedExclamationIcon from "../../../../../assets/client/red-error-exclamation.svg?react";
import ShieldIcon from "../../../../../assets/client/shield.svg?react";
import { ErrorScreen } from "../../../../../components/ErrorScreen";
import { GenericResponseScreen } from "../../../../../components/GenericResponseScreen";
import { closeNubankWebview } from "../../../../../utils/nubankHandoff";
import { PaymentEvent } from "../../../../states/payments";
import { getEnrollmentRedirectUrl } from "../../../../states/payments/selectors";
import { NubankPaymentChildState } from "../../../../states/payments/types";
import { LoadingScreen } from "../../../LoadingScreen";
import styles from "./styles.module.scss";

export const AuthConsentScreen = () => {
  const { t } = useI18nContext();
  const history = useHistory();
  const enrollmentRedirectUrl = useSelector(getEnrollmentRedirectUrl);
  const [state, send] = useCheckoutState();
  const nestedChild = getNestedChildState(state.value);

  const handleGoBack = () => {
    history.goBack();
  };

  if (nestedChild === NubankPaymentChildState.failure) {
    return (
      <GenericResponseScreen
        title={t("nupay.cancelEnrollment.header")}
        subtitle={t("nupay.cancelEnrollment.subheader")}
        primaryButtonText={t("nupay.cancelEnrollment.cancelAndReturnButton")}
        primaryOnClick={closeNubankWebview}
        secondaryButtonText={t("tryAgain")}
        secondaryOnClick={() => send(PaymentEvent.TRY_AGAIN)}
        onBack={closeNubankWebview}
        className={clsx("warn", styles.cancelScreenFloatingSection)}
        image={<RedExclamationIcon className="response-image" />}
        backIcon={<CloseIcon className={styles.cancelScreenCloseIcon} />}
      />
    );
  }

  if (
    [NubankPaymentChildState.success, NubankPaymentChildState.loading].includes(
      nestedChild as NubankPaymentChildState
    )
  ) {
    return <LoadingScreen />;
  }

  if (nestedChild === NubankPaymentChildState.error) {
    return (
      <ErrorScreen
        className="warn"
        title={t("nupay.authorizationErrorTitle")}
        subtitle={t("nupay.authorizationErrorSubheader")}
        primaryButtonText={t("cancel")}
        primaryOnClick={handleGoBack}
        secondaryButtonText={t("tryAgain")}
        secondaryOnClick={() => send(PaymentEvent.TRY_AGAIN)}
        onBack={handleGoBack}
      />
    );
  }

  const onClick = () => {
    // Initiate handoff to nubank
    if (enrollmentRedirectUrl) {
      send(PaymentEvent.GO_TO_LOADING);
      window.location.replace(enrollmentRedirectUrl);
    } else {
      console.log("no redirectUrl defined");
    }
    return null;
  };

  return (
    <div className={styles.authConsentScreen}>
      <div className="nupay-header">
        <ButtonWrap className="back-button" onClick={handleGoBack}>
          <ArrowBack className="arrow-back-icon" />
        </ButtonWrap>
      </div>
      <div className="auth-consent-content">
        <PasswordIcon className="pin-pad-image" />
        <h1>{t("nupay.requestAuthHeader")}</h1>
        <div className="value-props-list">
          <div className="value-props-item">
            <MoneyOutIcon className="value-props-icon" />
            <p>{t("nupay.requestAuthSubtitle1")}</p>
          </div>
          <div className="value-props-item">
            <ShieldIcon className="value-props-icon" />
            <p>{t("nupay.requestAuthSubtitle2")}</p>
          </div>
          <div className="value-props-item">
            <CardTransferIcon className="value-props-icon" />
            <p>{t("nupay.requestAuthSubtitle3")}</p>
          </div>
        </div>
        <MobileFloatingSection
          primaryButton={{
            children: t("nupay.requestAuthButton"),
            onClick: onClick,
          }}
        />
      </div>
    </div>
  );
};
